/**
 * Session Monitor
 *
 * Browse the caller's own sessions (clean, ACL-gated GET /sessions — no
 * cross-user filter), with the full conversation — messages, citations,
 * confidence — plus per-message tools-fired in the right panel (the shared
 * SessionDetailPanel, same renderer as QueryExplorer/FeedbackExplorer).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessions, useSession } from '@/hooks/useSessions';
import { useSessionQueryLogs } from '@/hooks/useSessionQueryLogs';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SessionDetailPanel } from './SessionDetailPanel';
import { safeFormatDistanceToNow, dateFnsLocale } from '@/utils/dates';
import { cn } from '@/lib/utils';

export function SessionMonitor({ initialSessionId }: { initialSessionId?: string | null }) {
  const { t, i18n } = useTranslation();
  const dateLocale = dateFnsLocale(i18n.language);
  const [selectedSessionId, setSelectedSessionId] = useState(initialSessionId ?? null);

  const {
    data: sessions,
    isLoading: sessionsLoading,
    isError: sessionsError,
    refetch: refetchSessions,
  } = useSessions();

  const {
    data: sessionDetail,
    isLoading: sessionLoading,
    error: sessionError,
    refetch: refetchSession,
  } = useSession(selectedSessionId ?? '');

  const {
    data: traces,
    isLoading: tracesLoading,
    isError: tracesError,
  } = useSessionQueryLogs(selectedSessionId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[600px]">
      {/* Sessions List */}
      <div className="bg-white rounded-xl border border-neutral-200 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {sessionsError ? (
              <div className="text-center py-8 text-sm space-y-2">
                <p className="text-red-600">{t('admin.sessionMonitor.loadError')}</p>
                <button
                  onClick={() => void refetchSessions()}
                  className="text-primary hover:underline"
                >
                  {t('actions.retry')}
                </button>
              </div>
            ) : sessionsLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="p-3 rounded-lg">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))
            ) : !sessions || sessions.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 text-sm">
                {t('admin.sessionMonitor.empty')}
              </div>
            ) : (
              sessions.map((session) => (
                <button
                  key={session.id}
                  onClick={() => setSelectedSessionId(session.id)}
                  className={cn(
                    'w-full text-left p-3 rounded-lg transition-colors',
                    selectedSessionId === session.id
                      ? 'bg-primary/10 border border-primary/20'
                      : 'hover:bg-neutral-50',
                  )}
                >
                  <p className="text-sm font-medium text-neutral-900 truncate">
                    {session.title || t('admin.sessionDetail.untitled')}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-neutral-500 mt-1">
                    <span>
                      {safeFormatDistanceToNow(
                        session.updated_at,
                        { addSuffix: true, locale: dateLocale },
                        '',
                      )}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Session Detail — shared panel (messages + citations + confidence + tool traces) */}
      <div className="bg-white rounded-xl border border-neutral-200 flex flex-col overflow-hidden">
        {!selectedSessionId ? (
          <div className="flex-1 flex items-center justify-center text-neutral-400 text-sm">
            {t('admin.sessionMonitor.selectPrompt')}
          </div>
        ) : (
          <SessionDetailPanel
            session={sessionDetail}
            isLoading={sessionLoading}
            error={sessionError}
            onRetry={() => void refetchSession()}
            traces={traces}
            tracesLoading={tracesLoading}
            tracesError={tracesError}
          />
        )}
      </div>
    </div>
  );
}
